import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, List, GripVertical, Rows3, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

const COLS = [
  { key: "planning",    label: "Planning",    tone: "slate"   },
  { key: "in_progress", label: "In progress", tone: "blue"    },
  { key: "on_hold",     label: "On hold",     tone: "amber"   },
  { key: "completed",   label: "Completed",   tone: "emerald" },
  { key: "cancelled",   label: "Cancelled",   tone: "rose"    },
];
const KINDS = ["generic", "website", "mobile_app", "seo", "uniform", "nfc"];
const empty = { name: "", client_id: "", kind: "generic", status: "planning", start_date: "", end_date: "", budget: 0, description: "" };

export default function ProjectsKanban() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [dragging, setDragging] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        api.get("/projects"),
        api.get("/crm/clients").catch(() => ({ data: [] })),
      ]);
      setItems(p.data || []);
      setClients(c.data || []);
    } catch (error) {
      console.error("Failed to load kanban board:", error);
      toast.error("Failed to load kanban board");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const byStatus = useMemo(() => {
    const map = Object.fromEntries(COLS.map((c) => [c.key, []]));
    (items || []).forEach((p) => {
      const k = COLS.find((c) => c.key === p.status) ? p.status : "planning";
      map[k].push(p);
    });
    return map;
  }, [items]);

  const clientName = (id) => clients.find((c) => c.client_id === id)?.name || "";

  const move = async (project, newStatus) => {
    if (!project || project.status === newStatus) return;
    setItems((cur) => cur.map((p) => p.project_id === project.project_id ? { ...p, status: newStatus } : p));
    try {
      await api.patch(`/projects/${project.project_id}`, { status: newStatus });
      toast.success(`Moved to ${COLS.find((c) => c.key === newStatus).label}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load();
    }
  };

  const submit = async () => {
    try {
      const payload = { ...form, budget: Number(form.budget || 0) };
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      if (editing) await api.patch(`/projects/${editing.project_id}`, payload);
      else await api.post("/projects", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (project) => {
    if (!window.confirm(`Delete "${project.name}"?`)) return;
    try {
      await api.delete(`/projects/${project.project_id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout
      title="Projects · Kanban"
      subtitle="Drag cards between columns to update status"
      right={
        <div className="flex items-center gap-2">
          <Link data-testid="kanban-to-list" to="/app/projects" className="btn-scale h-9 px-3 rounded-md border border-slate-700 text-slate-300 text-sm inline-flex items-center gap-2 hover:bg-slate-800">
            <List className="w-4 h-4" /> List view
          </Link>
          <button
            data-testid="kanban-new"
            onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
            className="btn-scale h-9 px-3 rounded-md bg-blue-500 text-white font-medium text-sm flex items-center gap-2 hover:bg-blue-400"
          >
            <Plus className="w-4 h-4" /> New project
          </button>
        </div>
      }
    >
      <div className="flex gap-4 overflow-x-auto pb-4 -mx-1 px-1 snap-x" data-testid="kanban-board">
        {COLS.map((col) => {
          const cards = byStatus[col.key] || [];
          const isOver = overCol === col.key;
          return (
            <div
              key={col.key}
              data-testid={`kanban-col-${col.key}`}
              onDragOver={(e) => { e.preventDefault(); setOverCol(col.key); }}
              onDragLeave={() => setOverCol(null)}
              onDrop={(e) => {
                e.preventDefault();
                setOverCol(null);
                if (dragging) move(dragging, col.key);
                setDragging(null);
              }}
              className={`shrink-0 snap-start w-[264px] rounded-xl border p-3 flex flex-col gap-3 transition-colors ${isOver ? "border-blue-500 bg-blue-500/5" : "border-zinc-800 bg-zinc-900/40"}`}
            >
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full bg-${col.tone}-500`} />
                  <span className="text-sm font-semibold text-zinc-100">{col.label}</span>
                </div>
                <span className="text-xs text-zinc-500">{cards.length}</span>
              </div>

              {cards.length === 0 && (
                <div className="text-xs text-zinc-600 text-center py-8 border border-dashed border-zinc-800 rounded-lg">
                  Drop cards here
                </div>
              )}

              {cards.map((p) => (
                <div
                  key={p.project_id}
                  draggable
                  onDragStart={() => setDragging(p)}
                  onDragEnd={() => { setDragging(null); setOverCol(null); }}
                  data-testid={`kanban-card-${p.project_id}`}
                  className="group rounded-lg border border-zinc-800 bg-zinc-950 p-3 cursor-grab active:cursor-grabbing hover:border-zinc-700 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm text-zinc-100 font-medium truncate">{p.name}</div>
                      <div className="text-[11px] text-zinc-500 uppercase tracking-wide mt-0.5">{p.kind?.replace("_", " ")}</div>
                    </div>
                    <GripVertical className="w-3.5 h-3.5 text-zinc-600 shrink-0 mt-1" />
                  </div>
                  {clientName(p.client_id) && (
                    <div className="text-xs text-zinc-400 mt-2 truncate">{clientName(p.client_id)}</div>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <div className="text-xs text-blue-400 font-mono">${Number(p.budget || 0).toFixed(0)}</div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        data-testid={`kanban-edit-${p.project_id}`}
                        onClick={(e) => { e.stopPropagation(); setEditing(p); setForm({ ...empty, ...p }); setOpen(true); }}
                        className="h-6 w-6 grid place-items-center rounded text-zinc-400 hover:text-blue-300 hover:bg-zinc-800"
                      ><Pencil className="w-3 h-3" /></button>
                      <button
                        data-testid={`kanban-delete-${p.project_id}`}
                        onClick={(e) => { e.stopPropagation(); remove(p); }}
                        className="h-6 w-6 grid place-items-center rounded text-zinc-400 hover:text-rose-400 hover:bg-zinc-800"
                      ><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                  <MoveMenu current={p.status} onMove={(s) => move(p, s)} />
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl" data-testid="kanban-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit project" : "New project"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <label><span className="overline block mb-1.5">Name</span>
              <input data-testid="kanban-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            </label>
            <label><span className="overline block mb-1.5">Client</span>
              <select data-testid="kanban-field-client" value={form.client_id || ""} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
                <option value="">—</option>
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
              </select>
            </label>
            <label><span className="overline block mb-1.5">Kind</span>
              <select data-testid="kanban-field-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
                {KINDS.map((k) => <option key={k} value={k}>{k.replace("_", " ")}</option>)}
              </select>
            </label>
            <label><span className="overline block mb-1.5">Status</span>
              <select data-testid="kanban-field-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
                {COLS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>
            <label><span className="overline block mb-1.5">Budget ($)</span>
              <input data-testid="kanban-field-budget" type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            </label>
            <label><span className="overline block mb-1.5">Start</span>
              <input data-testid="kanban-field-start" type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            </label>
            <label className="md:col-span-2"><span className="overline block mb-1.5">Description</span>
              <textarea data-testid="kanban-field-desc" rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm" />
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-scale h-9 px-3 rounded-md border border-zinc-700 text-zinc-300 text-sm">Cancel</button>
            <button data-testid="kanban-save" onClick={submit} className="btn-scale h-9 px-4 rounded-md bg-blue-500 text-white text-sm font-medium hover:bg-blue-400">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function MoveMenu({ current, onMove }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative mt-2">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="text-[11px] text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1"
      >
        <ArrowLeftRight className="w-3 h-3" /> Move
      </button>
      {open && (
        <div className="absolute z-20 mt-1 rounded-md border border-zinc-800 bg-zinc-950 p-1 shadow-xl">
          {COLS.filter((c) => c.key !== current).map((c) => (
            <button
              key={c.key}
              onClick={(e) => { e.stopPropagation(); setOpen(false); onMove(c.key); }}
              className="block w-full text-left px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800 rounded"
            >{c.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}
